interface DemoDictionarySummarySeed {
  dictionary_id: string;
  abbreviation: string;
  title: string;
  description: string;
  comments: string | null;
  entry_count: number;
  rating: number;
  review_count: number;
  tone: 'gold' | 'ocean' | 'sage' | 'plum' | 'slate';
  is_featured: boolean;
  featured_topic: string;
  scope_label: string;
  accent_label: string;
  strong?: boolean;
}

interface DemoDictionaryWordSeed {
  topic: string;
  letter: string;
  hit_count: number;
  dictionary_count: number;
  dictionary_ids?: string[];
}

interface DemoDictionaryEntrySeed {
  definition?: string;
  subtitle: string;
  pronunciation: string | null;
  strongs_reference: string | null;
  summary: string;
  sections: Array<{
    id: string;
    heading: string;
    paragraphs: string[];
  }>;
  images: Array<{
    id: string;
    src: string;
    alt: string;
  }>;
  related_topics: string[];
  concordance: Array<{
    label: string;
    value: string;
  }>;
  previous_word: string | null;
  next_word: string | null;
  word_count: number;
  page: number;
  total_pages: number;
  atlas?: {
    placename?: string;
    atlas_id?: string;
    latitude?: string;
    longitude?: string;
    full_coordinates?: string;
  };
}

interface DemoDictionarySeed {
  dictionaries: DemoDictionarySummarySeed[];
  word_index: DemoDictionaryWordSeed[];
  entries: Record<string, DemoDictionaryEntrySeed>;
}

interface DemoStudyTrackingSession {
  sessionKey: string;
  userId: string;
  resourceType: string;
  resourceId: string;
  entryKey: string;
  entryLabel: string | null;
  durationSeconds: number;
  scrollProgress: number;
  positionToken: string | null;
  lastActiveAt: number;
  startedAt: number;
  status: 'active' | 'ended';
}

type MockResponse = {
  status?: number;
  body: unknown;
};

type StudyTrackingPayload = {
  duration_seconds?: number;
  scroll_progress?: number;
  position_token?: string | null;
  resource_type?: string;
  resource_id?: string;
  entry_key?: string;
  entry_label?: string;
};

export class DictionaryMockState {
  private dictionarySeed: DemoDictionarySeed | null = null;
  private studyTrackingSessions: DemoStudyTrackingSession[] = [];

  constructor(private readonly loadJson: <T>(path: string) => Promise<T>) {}

  async handleDictionaryHome(query: {
    tab: string;
    searchQuery: string;
    dictionaryId: string;
    letter: string;
    searchScope: string;
    matchMode: string;
    strongOnly: boolean;
    atlasOnly: boolean;
    wordLimit: number;
    wordOffset: number;
  }): Promise<MockResponse> {
    const seed = await this.getSeed();
    const tab = this.resolveTab(query.tab);
    const searchQuery = query.searchQuery.trim();
    const wordLimit = Math.max(1, query.wordLimit || 24);
    const wordOffset = Math.max(0, query.wordOffset || 0);
    const baseWords = seed.word_index.filter((item) => {
      const availableDictionaryIds = this.resolveAvailableDictionaryIds(
        item,
        seed.dictionaries
      ).filter((dictionaryId) => {
        if (query.dictionaryId && dictionaryId !== query.dictionaryId) {
          return false;
        }

        const dictionary = seed.dictionaries.find(
          (candidate) => candidate.dictionary_id === dictionaryId
        );

        return !query.strongOnly || !!dictionary?.strong;
      });

      return (
        availableDictionaryIds.length > 0 &&
        this.matchesEntryFilters(
          item.topic,
          seed.entries[item.topic],
          searchQuery,
          query.searchScope,
          query.matchMode,
          query.atlasOnly
        )
      );
    });
    const selectedLetter = this.resolveLetter(query.letter, baseWords);
    const filteredWords = searchQuery
      ? baseWords
      : baseWords.filter((item) => item.letter === selectedLetter);
    const visibleWords = filteredWords.slice(
      wordOffset,
      wordOffset + wordLimit
    );
    const wordIndex = visibleWords.map((item) =>
      this.toDictionaryWord(
        item,
        seed.dictionaries,
        query.dictionaryId || null,
        query.strongOnly
      )
    );
    const filteredDictionaries = this.filterDictionaries(
      seed.dictionaries,
      tab
    ).filter((item) => !query.strongOnly || !!item.strong);
    const dictionaries =
      (searchQuery || query.atlasOnly) && wordIndex.length === 0
        ? []
        : filteredDictionaries.map((item) => this.toDictionarySummary(item));
    const selectedDictionaryId = dictionaries.some(
      (item) => item.dictionaryId === query.dictionaryId
    )
      ? query.dictionaryId
      : null;
    const spotlightDictionaryId =
      dictionaries.find((item) => item.isFeatured)?.dictionaryId ??
      dictionaries[0]?.dictionaryId ??
      null;

    return {
      body: {
        success: true,
        message: '',
        data: {
          activeTab: tab,
          dictionaries,
          alphabet: this.buildAlphabet(baseWords),
          wordIndex,
          wordIndexMeta: {
            offset: wordOffset,
            limit: wordLimit,
            hasMore: wordOffset + wordIndex.length < filteredWords.length,
          },
          spotlightDictionaryId,
          selectedDictionaryId,
          searchQuery,
          selectedLetter,
          filters: {
            searchScope: this.normalizeSearchScope(query.searchScope),
            matchMode: this.normalizeMatchMode(query.matchMode),
            strongOnly: query.strongOnly,
            atlasOnly: query.atlasOnly,
          },
        },
      },
    };
  }

  async handleDictionaryEntry(query: {
    dictionaryId: string;
    topic: string;
    wordLetter: string;
    view: 'single' | 'all';
    wordLimit: number;
    wordOffset: number;
  }): Promise<MockResponse> {
    const seed = await this.getSeed();
    const topic = query.topic.trim() || 'Aaron';
    const selectedLetter =
      query.wordLetter.trim().toUpperCase().slice(0, 1) ||
      topic.slice(0, 1).toUpperCase() ||
      'A';
    const wordLimit = Math.max(1, query.wordLimit || 24);
    const wordOffset = Math.max(0, query.wordOffset || 0);
    const dictionarySeed =
      seed.dictionaries.find(
        (item) => item.dictionary_id === query.dictionaryId
      ) ?? seed.dictionaries[0];
    const entrySeed =
      seed.entries[topic] ?? this.buildFallbackEntry(seed, topic);
    const entryWord = seed.word_index.find(
      (item) => item.topic.toLowerCase() === topic.toLowerCase()
    );
    const availableDictionaries = entryWord
      ? this.resolveWordSources(entryWord, seed.dictionaries)
      : undefined;
    const filteredWordIndex = seed.word_index.filter(
      (item) => item.letter === selectedLetter
    );
    const visibleWordIndex = filteredWordIndex.slice(
      wordOffset,
      wordOffset + wordLimit
    );

    return {
      body: {
        success: true,
        message: '',
        data: {
          dictionary: this.toDictionarySummary(dictionarySeed),
          dictionaries: seed.dictionaries.map((item) =>
            this.toDictionarySummary(item)
          ),
          viewMode:
            query.view === 'all' && (availableDictionaries?.length ?? 0) > 1
              ? 'all'
              : 'single',
          sourceEntries:
            query.view === 'all' && (availableDictionaries?.length ?? 0) > 1
              ? availableDictionaries?.map((source) => {
                  const sourceDictionarySeed =
                    seed.dictionaries.find(
                      (item) => item.dictionary_id === source.dictionaryId
                    ) ?? dictionarySeed;
                  const sourceEntrySeed =
                    seed.entries[topic] ?? this.buildFallbackEntry(seed, topic);

                  return {
                    dictionary: this.toDictionarySummary(sourceDictionarySeed),
                    entry: {
                      topic,
                      definition:
                        sourceEntrySeed.definition ??
                        this.buildDefinitionText(sourceEntrySeed),
                      subtitle: sourceEntrySeed.subtitle,
                      wordCount: sourceEntrySeed.word_count,
                    },
                  };
                })
              : undefined,
          alphabet: this.buildAlphabet(seed.word_index),
          wordIndex: visibleWordIndex.map((item) =>
            this.toDictionaryWord(
              item,
              seed.dictionaries,
              dictionarySeed.dictionary_id
            )
          ),
          wordIndexMeta: {
            offset: wordOffset,
            limit: wordLimit,
            hasMore:
              wordOffset + visibleWordIndex.length < filteredWordIndex.length,
          },
          selectedLetter,
          entry: {
            topic,
            definition:
              entrySeed.definition ?? this.buildDefinitionText(entrySeed),
            subtitle: entrySeed.subtitle,
            pronunciation: entrySeed.pronunciation,
            strongsReference: entrySeed.strongs_reference,
            summary: entrySeed.summary,
            sections: entrySeed.sections,
            images: entrySeed.images,
            relatedTopics: entrySeed.related_topics,
            concordance: entrySeed.concordance,
            previousWord: entrySeed.previous_word,
            nextWord: entrySeed.next_word,
            wordCount: entrySeed.word_count,
            page: entrySeed.page,
            totalPages: entrySeed.total_pages,
            availableDictionaries,
            atlas: entrySeed.atlas
              ? {
                  placename: entrySeed.atlas.placename,
                  atlasId: entrySeed.atlas.atlas_id,
                  latitude: entrySeed.atlas.latitude,
                  longitude: entrySeed.atlas.longitude,
                  fullCoordinates: entrySeed.atlas.full_coordinates,
                }
              : undefined,
          },
        },
      },
    };
  }

  handleResume(query: {
    userId: string;
    resourceType: string;
    resourceId: string;
    entryKey: string;
  }): MockResponse {
    const session = this.studyTrackingSessions
      .filter((item) => item.userId === query.userId)
      .filter((item) => item.resourceType === query.resourceType)
      .filter((item) => item.resourceId === query.resourceId)
      .filter((item) => item.entryKey === query.entryKey)
      .sort((left, right) => right.lastActiveAt - left.lastActiveAt)[0];

    return {
      body: {
        success: true,
        message: '',
        data: session ? this.toTrackingResume(session) : null,
      },
    };
  }

  startSession(userId: string, payload: StudyTrackingPayload): MockResponse {
    const resourceType = String(payload.resource_type || 'dictionary');
    const resourceId = String(payload.resource_id || '');
    const entryKey = String(payload.entry_key || '');
    const now = Math.floor(Date.now() / 1000);
    const existing = this.studyTrackingSessions.find(
      (item) =>
        item.userId === userId &&
        item.resourceType === resourceType &&
        item.resourceId === resourceId &&
        item.entryKey === entryKey &&
        item.status === 'active'
    );

    if (existing) {
      existing.lastActiveAt = now;
      return {
        body: {
          success: true,
          message: 'Tracking session started',
          data: {
            sessionKey: existing.sessionKey,
            joinedExisting: true,
            currentDuration: existing.durationSeconds,
          },
        },
      };
    }

    const session: DemoStudyTrackingSession = {
      sessionKey: `study-${Date.now()}`,
      userId,
      resourceType,
      resourceId,
      entryKey,
      entryLabel: String(payload.entry_label || entryKey || ''),
      durationSeconds: 0,
      scrollProgress: Number(payload.scroll_progress || 0),
      positionToken: payload.position_token ?? null,
      lastActiveAt: now,
      startedAt: now,
      status: 'active',
    };
    this.studyTrackingSessions.push(session);

    return {
      body: {
        success: true,
        message: 'Tracking session started',
        data: {
          sessionKey: session.sessionKey,
          joinedExisting: false,
          currentDuration: 0,
        },
      },
    };
  }

  updateSession(
    sessionKey: string,
    payload: StudyTrackingPayload
  ): MockResponse {
    const session = this.studyTrackingSessions.find(
      (item) => item.sessionKey === sessionKey
    );

    if (!session) {
      return {
        status: 404,
        body: {
          success: false,
          message: 'Tracking session not found',
          data: null,
        },
      };
    }

    session.durationSeconds = Math.max(
      session.durationSeconds,
      Number(payload.duration_seconds || 0)
    );
    session.scrollProgress = Number(payload.scroll_progress || 0);
    session.positionToken = payload.position_token ?? null;
    session.lastActiveAt = Math.floor(Date.now() / 1000);

    return {
      body: {
        success: true,
        message: 'Tracking session updated',
        data: {
          currentDuration: session.durationSeconds,
        },
      },
    };
  }

  endSession(sessionKey: string, payload: StudyTrackingPayload): MockResponse {
    const session = this.studyTrackingSessions.find(
      (item) => item.sessionKey === sessionKey
    );

    if (!session) {
      return {
        status: 404,
        body: {
          success: false,
          message: 'Tracking session not found',
          data: null,
        },
      };
    }

    session.durationSeconds = Math.max(
      session.durationSeconds,
      Number(payload.duration_seconds || 0)
    );
    session.scrollProgress = Number(payload.scroll_progress || 0);
    session.positionToken = payload.position_token ?? null;
    session.lastActiveAt = Math.floor(Date.now() / 1000);
    session.status = 'ended';

    return {
      body: {
        success: true,
        message: 'Tracking session ended',
        data: {
          finalDuration: session.durationSeconds,
        },
      },
    };
  }

  private async getSeed(): Promise<DemoDictionarySeed> {
    if (this.dictionarySeed) {
      return this.dictionarySeed;
    }

    this.dictionarySeed = await this.loadJson<DemoDictionarySeed>(
      '/assets/mocks/dictionary/seed.json'
    );
    return this.dictionarySeed;
  }

  private resolveTab(value: string): 'popular' | 'recent' | 'mine' | 'all' {
    if (value === 'recent' || value === 'mine' || value === 'all') {
      return value;
    }

    return 'popular';
  }

  private resolveLetter(
    requestedLetter: string,
    words: DemoDictionaryWordSeed[]
  ): string {
    const candidate = requestedLetter.trim().toUpperCase().slice(0, 1);
    return candidate || words[0]?.letter || 'A';
  }

  private filterDictionaries(
    dictionaries: DemoDictionarySummarySeed[],
    tab: 'popular' | 'recent' | 'mine' | 'all'
  ): DemoDictionarySummarySeed[] {
    if (tab === 'mine') {
      return dictionaries.filter(
        (item) => item.rating >= 4.6 || item.is_featured
      );
    }

    if (tab === 'recent') {
      return [...dictionaries].sort((left, right) =>
        right.title.localeCompare(left.title)
      );
    }

    if (tab === 'all') {
      return dictionaries;
    }

    return [...dictionaries].sort((left, right) => right.rating - left.rating);
  }

  private matchesEntryFilters(
    topic: string,
    entry: DemoDictionaryEntrySeed | undefined,
    searchQuery: string,
    searchScope: string,
    matchMode: string,
    atlasOnly: boolean
  ): boolean {
    if (atlasOnly && !entry?.atlas) {
      return false;
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return true;
    }

    const scope = this.normalizeSearchScope(searchScope);
    const fields: string[] = [];
    if (scope === 'topic' || scope === 'all') {
      fields.push(topic);
    }
    if (scope === 'definition' || scope === 'all') {
      fields.push(entry?.definition ?? this.buildDefinitionText(entry));
    }

    return fields.some((field) =>
      this.matchesText(
        field,
        normalizedQuery,
        this.normalizeMatchMode(matchMode)
      )
    );
  }

  private matchesText(
    value: string,
    normalizedQuery: string,
    matchMode: 'contains' | 'starts_with' | 'exact'
  ): boolean {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return false;
    }

    if (matchMode === 'exact') {
      return normalizedValue === normalizedQuery;
    }

    if (matchMode === 'starts_with') {
      return normalizedValue.startsWith(normalizedQuery);
    }

    return normalizedValue.includes(normalizedQuery);
  }

  private normalizeSearchScope(value: string): 'topic' | 'definition' | 'all' {
    if (value === 'definition' || value === 'all') {
      return value;
    }

    return 'topic';
  }

  private normalizeMatchMode(
    value: string
  ): 'contains' | 'starts_with' | 'exact' {
    if (value === 'starts_with' || value === 'exact') {
      return value;
    }

    return 'contains';
  }

  private buildAlphabet(words: DemoDictionaryWordSeed[]) {
    const counts = new Map<string, number>();
    for (const item of words) {
      counts.set(item.letter, (counts.get(item.letter) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([letter, count]) => ({ letter, count }));
  }

  private toDictionarySummary(seed: DemoDictionarySummarySeed) {
    return {
      dictionaryId: seed.dictionary_id,
      abbreviation: seed.abbreviation,
      title: seed.title,
      description: seed.description,
      comments: seed.comments,
      entryCount: seed.entry_count,
      rating: seed.rating,
      reviewCount: seed.review_count,
      tone: seed.tone,
      isFeatured: seed.is_featured,
      featuredTopic: seed.featured_topic,
      scopeLabel: seed.scope_label,
      accentLabel: seed.accent_label,
    };
  }

  private toDictionaryWord(
    seed: DemoDictionaryWordSeed,
    dictionaries: DemoDictionarySummarySeed[],
    selectedDictionaryId: string | null = null,
    strongOnly = false
  ) {
    const sources = this.resolveWordSources(
      seed,
      dictionaries,
      selectedDictionaryId,
      strongOnly
    );

    return {
      topic: seed.topic,
      letter: seed.letter,
      hitCount: seed.hit_count,
      dictionaryCount: sources.length || seed.dictionary_count,
      availableDictionaries: sources,
    };
  }

  private resolveAvailableDictionaryIds(
    seed: DemoDictionaryWordSeed,
    dictionaries: DemoDictionarySummarySeed[]
  ): string[] {
    if (seed.dictionary_ids?.length) {
      return seed.dictionary_ids;
    }

    return dictionaries
      .slice(
        0,
        Math.max(1, Math.min(seed.dictionary_count, dictionaries.length))
      )
      .map((item) => item.dictionary_id);
  }

  private resolveWordSources(
    seed: DemoDictionaryWordSeed,
    dictionaries: DemoDictionarySummarySeed[],
    selectedDictionaryId: string | null = null,
    strongOnly = false
  ) {
    return this.resolveAvailableDictionaryIds(seed, dictionaries)
      .map((dictionaryId) =>
        dictionaries.find((item) => item.dictionary_id === dictionaryId)
      )
      .filter((item): item is DemoDictionarySummarySeed => !!item)
      .filter(
        (item) =>
          !selectedDictionaryId || item.dictionary_id === selectedDictionaryId
      )
      .filter((item) => !strongOnly || !!item.strong)
      .map((item) => ({
        dictionaryId: item.dictionary_id,
        abbreviation: item.abbreviation,
        title: item.title,
        strong: !!item.strong,
      }));
  }

  private buildFallbackEntry(
    seed: DemoDictionarySeed,
    topic: string
  ): DemoDictionaryEntrySeed {
    const fallbackImages = seed.entries['Aaron']?.images ?? [];
    const initialLetter = topic.slice(0, 1).toUpperCase();
    const relatedTopics = seed.word_index
      .filter((item) => item.letter === initialLetter)
      .map((item) => item.topic)
      .filter((item) => item !== topic)
      .slice(0, 5);

    return {
      definition:
        `${topic} is presented here as a premium placeholder entry so the prototype stays visually complete even when the live dataset has not yet been connected for this topic. ` +
        `${topic} should support the same reading layout, navigation depth, and mobile polish as the richer seeded entries.`,
      subtitle: `${topic} in biblical study and cross-reference reading`,
      pronunciation: null,
      strongs_reference: null,
      summary: `${topic} is presented here as a premium placeholder entry so the prototype stays visually complete even when the live dataset has not yet been connected for this topic.`,
      sections: [
        {
          id: 'overview',
          heading: 'Overview',
          paragraphs: [
            `${topic} should support the same reading layout, navigation depth, and mobile polish as the richer seeded entries.`,
            'This fallback content keeps the prototype interactive while preserving the final API shape.',
          ],
        },
        {
          id: 'study-notes',
          heading: 'Study Notes',
          paragraphs: [
            'The final implementation should replace this fallback with live dictionary data, commentary-linked references, and persisted resume state from the backend.',
            'Tracking should continue to record where the user stopped, how long the session lasted, and which section was in focus.',
          ],
        },
      ],
      images: fallbackImages.slice(0, 3),
      related_topics: relatedTopics,
      concordance: [
        { label: 'Study', value: 'Premium prototype' },
        { label: 'State', value: 'Fallback entry' },
      ],
      previous_word: relatedTopics[0] ?? null,
      next_word: relatedTopics[1] ?? null,
      word_count: 96,
      page: 1,
      total_pages: 8,
    };
  }

  private buildDefinitionText(
    entry: DemoDictionaryEntrySeed | undefined
  ): string {
    if (!entry) {
      return '';
    }

    if (entry.definition?.trim()) {
      return entry.definition.trim();
    }

    return entry.sections
      .flatMap((section) => section.paragraphs)
      .join(' ')
      .trim();
  }

  private toTrackingResume(session: DemoStudyTrackingSession) {
    return {
      sessionKey: session.sessionKey,
      resourceType: session.resourceType,
      resourceId: session.resourceId,
      entryKey: session.entryKey,
      entryLabel: session.entryLabel,
      durationSeconds: session.durationSeconds,
      scrollProgress: session.scrollProgress,
      positionToken: session.positionToken,
      lastActiveAt: session.lastActiveAt,
      startedAt: session.startedAt,
    };
  }
}
